import type { CardData, Suit } from './types';

// A scoring rule is a pure description of how to compare two players'
// state to decide a winner. Used by both Location cards and Mission
// cards — they share the same primitive logic, only metadata differs.
// Two families:
//  - SUIT-BOUND kinds carry a `seed` and are used ONLY by Missions (each Mission
//    card is drafted alongside its suit, so a suit-specific goal is coherent).
//  - GENERIC kinds carry no `seed` and are used ONLY by Locations (the location
//    is the shared scenario — it must be valid whatever the two players drafted).
// The split is enforced by tests, see missions.test.ts / locations.test.ts.
export type ScoringRuleKind =
  // --- suit-bound (Missions) ---
  | 'seed_count'        // count of CARDS of a given suit (1 card = 1, regardless of value)
  | 'seed_resources'    // sum of RESOURCES of a given suit (reads resources dict, not discard)
  | 'specific_card'     // owns a specific (suit, value) — 1 or 0
  | 'seed_high_count'   // count of CARDS of a given suit with value >= `value` (threshold)
  | 'seed_low_count'    // count of CARDS of a given suit with value <= `value` (threshold)
  // --- generic (Locations) ---
  | 'value_count'       // count of cards with a given numeric value
  | 'cards_count'       // total count of cards in discard
  | 'total_resources';  // sum of resources across all suits (reads resources dict)

export interface ScoringRule {
  kind: ScoringRuleKind;
  seed?: Suit;       // seed_count, seed_resources, specific_card, seed_high_count, seed_low_count
  value?: number;    // value_count, specific_card (exact); seed_high_count, seed_low_count (threshold)
  reverse?: boolean; // flip: lower count wins
}

// Per-player slice of state needed for scoring. Includes both the captured-cards
// pile (for card-based rules) AND the resources dict (for resource-based rules).
// Resource-based rules MUST read from `resources` because card effects can add,
// remove, multiply, or transform resources without going through the discard pile
// (e.g. drug 3 grants +1 drug on play; cars 5 doubles its value to the captor).
// Re-deriving resources from discard would miss all those modifications.
export interface ScoringContext {
  discard: CardData[];
  resources: Record<string, number>;
}

// Returns a numeric "fitness" for one player against the rule.
// Higher = better, unless rule.reverse is true (then lower = better).
// Card-based kinds filter discard to Hand-only (Mission cards may end up in
// discard as future salvage but they're rule carriers, not collected resources).
export function evaluateScoringRule(rule: ScoringRule, ctx: ScoringContext): number {
  if (!rule || !rule.kind) return 0;
  const cards = ctx.discard.filter(c => c.type === 'Hand');
  switch (rule.kind) {
    case 'seed_count': {
      // Count of cards of the given suit in the captured pile.
      const seed = rule.seed;
      if (seed === undefined) return 0;
      return cards.filter(c => c.suit === seed).length;
    }
    case 'seed_resources': {
      // Truth lives in the resources dict, not in card values. This way any
      // effect that adds, removes, multiplies, or transforms resources is
      // honored automatically.
      const seed = rule.seed;
      if (seed === undefined) return 0;
      return ctx.resources[seed] || 0;
    }
    case 'seed_high_count': {
      // Count of suit cards at or above the value threshold ("the heavy stuff").
      const seed = rule.seed;
      if (seed === undefined || rule.value === undefined) return 0;
      return cards.filter(c => c.suit === seed && c.value >= rule.value!).length;
    }
    case 'seed_low_count': {
      // Count of suit cards at or below the value threshold ("the small-time stuff").
      const seed = rule.seed;
      if (seed === undefined || rule.value === undefined) return 0;
      return cards.filter(c => c.suit === seed && c.value <= rule.value!).length;
    }
    case 'value_count':
      return cards.filter(c => c.value === rule.value).length;
    case 'cards_count':
      return cards.length;
    case 'specific_card':
      return cards.some(c => c.suit === rule.seed && c.value === rule.value) ? 1 : 0;
    case 'total_resources':
      // Sum across all resource buckets — same reasoning as seed_resources.
      return Object.values(ctx.resources).reduce((s, v) => s + (v || 0), 0);
  }
}

// Returns the winning playerID ('0' or '1'), or null on a tie.
export function decideWinner(
  rule: ScoringRule,
  ctx0: ScoringContext,
  ctx1: ScoringContext
): string | null {
  const c0 = evaluateScoringRule(rule, ctx0);
  const c1 = evaluateScoringRule(rule, ctx1);
  if (c0 === c1) return null;
  if (rule.reverse) return c0 < c1 ? '0' : '1';
  return c0 > c1 ? '0' : '1';
}
